
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MessageCircle, Users, BarChart, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const Landing = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="border-b px-8 py-4 flex items-center justify-between bg-white">
        <div className="font-bold text-xl">Marketing Mary</div>
        <div className="flex items-center space-x-4">
          <Link to="/pricing" className="text-primary hover:text-primary/80">Pricing</Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground">Features</Link>
          
          {user ? (
            <Button asChild>
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="outline" className="ml-2">
                <Link to="/auth">Login</Link>
              </Button>
              <Button asChild>
                <Link to="/auth?tab=signup">Sign Up</Link>
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary/50 to-background py-24 px-8 text-center">
        <h1 className="text-5xl font-bold mb-6 max-w-3xl mx-auto">
          Your AI <span className="gradient-text">Marketing Director</span> at a Fraction of the Cost
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          Marketing Mary analyzes your business, identifies your ideal customers, and provides expert marketing advice through a conversational interface.
        </p>
        <div className="flex justify-center space-x-4">
          <Button asChild size="lg" className="h-12 px-8">
            <Link to={user ? "/dashboard" : "/auth?tab=signup"}>
              {user ? "Go to Dashboard" : "Try for Free"}
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-8">
            <Link to="/">Watch Demo</Link>
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">How Marketing Mary Works</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our AI assistant analyzes your business and helps you understand your customers better than ever before.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
          <div className="bg-white rounded-lg p-8 shadow-sm border">
            <div className="bg-primary/10 p-3 rounded-full w-fit mb-6">
              <BarChart className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Website Analysis</h3>
            <p className="text-muted-foreground">
              We analyze your website to understand your business, products, and value proposition.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm border">
            <div className="bg-primary/10 p-3 rounded-full w-fit mb-6">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Buyer Personas</h3>
            <p className="text-muted-foreground">
              We identify 3-5 detailed buyer personas relevant to your business and industry.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm border">
            <div className="bg-primary/10 p-3 rounded-full w-fit mb-6">
              <MessageCircle className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">AI Conversations</h3>
            <p className="text-muted-foreground">
              Chat with Marketing Mary or your buyer personas to get marketing insights and advice.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white py-16 px-8 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Upgrade Your Marketing?</h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
          Join thousands of SMEs using Marketing Mary to improve their marketing strategy.
        </p>
        <Button asChild size="lg" variant="secondary" className="h-12 px-8">
          <Link to="/dashboard" className="flex items-center">
            Get Started <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </section>

      {/* Footer */}
      <footer className="bg-secondary py-12 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Marketing Mary</h3>
              <p className="text-muted-foreground">
                Your AI-powered marketing assistant for SMEs.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Features</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Website Analysis</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Buyer Personas</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">AI Chat</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Marketing Advice</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Blog</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Documentation</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Support</Link></li>
                <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">About</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Careers</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Privacy</Link></li>
                <li><Link to="/" className="text-muted-foreground hover:text-foreground">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-primary/10 text-center text-muted-foreground">
            <p>© 2025 Marketing Mary. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
